import { useState, useCallback } from "react";
import { Inbox, ChevronRight, MapPin, DollarSign, Truck, Loader2, Search } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MatchedLoadsModal, { type MatchedLoad } from "./MatchedLoadsModal";

const SAMPLE_LOADS: MatchedLoad[] = [
  { loadId: "demo-1", brokerName: "Coyote Logistics", originLocation: "Dallas, TX", destinationLocation: "Atlanta, GA", rate: 2350, miles: 780 },
  { loadId: "demo-2", brokerName: "TQL", originLocation: "Chicago, IL", destinationLocation: "Miami, FL", rate: 3100, miles: 1380 },
  { loadId: "demo-3", brokerName: "CH Robinson", originLocation: "Phoenix, AZ", destinationLocation: "Houston, TX", rate: 2150, miles: 1180 },
];

type ScanState = "idle" | "loading" | "done";

export default function AILoadMatchCard() {
  const { user } = useAuth();
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [matches, setMatches] = useState<MatchedLoad[]>([]);
  const [laneCount, setLaneCount] = useState(0);
  const [hasTrucks, setHasTrucks] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const runScan = useCallback(async () => {
    if (!user) return;
    setScanState("loading");
    try {
      const today = new Date().toISOString().split("T")[0];

      const { data: trucks } = await supabase
        .from("truck_postings")
        .select("id, equipment_type, origin_location, destination_location, available_from, available_to")
        .eq("created_by", user.id)
        .in("status", ["active", "available"])
        .gte("available_to", today);

      if (!trucks?.length) {
        setHasTrucks(false);
        setMatches([]);
        setLaneCount(0);
        setScanState("done");
        return;
      }
      setHasTrucks(true);

      const { data: loads } = await supabase
        .from("loads")
        .select("id, equipment_type, origin_city, origin_state, destination_city, destination_state, pickup_date, rate_amount, total_miles, company_id, companies(name)")
        .eq("status", "posted")
        .gte("pickup_date", today)
        .neq("created_by", user.id)
        .limit(500);

      const results: MatchedLoad[] = [];
      const seen = new Set<string>();
      const lanes = new Set<string>();

      if (loads?.length) {
        for (const truck of trucks) {
          const truckState = extractState(truck.origin_location);
          for (const load of loads as any[]) {
            const truckEq = (truck.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
            const loadEq = (load.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
            if (truckEq && loadEq && truckEq !== loadEq) continue;
            if (load.pickup_date < truck.available_from || load.pickup_date > truck.available_to) continue;
            if (truckState && load.origin_state && truckState !== load.origin_state) continue;
            if (seen.has(load.id)) continue;
            seen.add(load.id);

            const co = load.companies as any;
            lanes.add(`${load.origin_state}->${load.destination_state}`);

            results.push({
              loadId: load.id,
              brokerName: co?.name || "Independent Broker",
              originLocation: `${load.origin_city}, ${load.origin_state}`,
              destinationLocation: `${load.destination_city}, ${load.destination_state}`,
              originCity: load.origin_city,
              originState: load.origin_state,
              destCity: load.destination_city,
              destState: load.destination_state,
              rate: load.rate_amount ?? null,
              miles: load.total_miles ?? null,
              equipment: load.equipment_type ?? null,
              pickupDate: load.pickup_date ?? null,
            });
          }
        }
      }

      results.sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
      setMatches(results);
      setLaneCount(lanes.size);
      setScanState("done");
    } catch (err) {
      console.error("AILoadMatchCard scan error:", err);
      setScanState("done");
    }
  }, [user]);

  const count = matches.length;
  const topMatches = matches.slice(0, 3);

  const ratesWithRpm = matches
    .filter((m) => m.rate && m.miles && m.miles > 0)
    .map((m) => (m.rate as number) / (m.miles as number));
  const avgRpm = ratesWithRpm.length
    ? ratesWithRpm.reduce((s, v) => s + v, 0) / ratesWithRpm.length
    : 0;

  // Show demo preview only when scan completed with no trucks/no results.
  const showDemo = scanState === "done" && (!hasTrucks || count === 0);
  const displayMatches = scanState === "done" && count > 0 ? topMatches : SAMPLE_LOADS;
  const displayCount = scanState === "done" && count > 0 ? count : (showDemo ? 14 : 0);
  const displayLanes = scanState === "done" && count > 0 ? laneCount : (showDemo ? 6 : 0);
  const displayAvgRpm = scanState === "done" && count > 0 ? avgRpm : (showDemo ? 2.42 : 0);

  return (
    <>
      <PostLoadCard className="flex flex-col">
        <PostLoadCardHeader
          icon={<Inbox className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
          title="ATLAS Load Match"
          demoHintKey="section.aiLoadMatch"
          variant="atlas"
        />
        <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">
          <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(210 90% 50% / 0.08)" }}>
            <Inbox className="h-3 w-3 shrink-0" style={{ color: "hsl(210 80% 50%)" }} />
            <span className="text-[11px] font-semibold" style={{ color: "hsl(210 70% 42%)" }}>
              {scanState === "idle" && "Scan posted loads against your active trucks"}
              {scanState === "loading" && "Scanning…"}
              {scanState === "done" && count > 0 && `${count} load${count !== 1 ? "s" : ""} match your trucks`}
              {scanState === "done" && count === 0 && (hasTrucks ? "No matches right now" : "Post a truck to find matches")}
            </span>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {scanState === "idle" && "Atlas will compare your active truck postings against currently posted loads and surface lane matches."}
            {scanState === "loading" && "Comparing your active trucks against posted loads…"}
            {scanState === "done" && count > 0 && (
              <>ATLAS AI found <span className="font-semibold text-foreground">{count} loads</span> matching your active trucks.</>
            )}
            {scanState === "done" && count === 0 && hasTrucks && "Your trucks didn't match any posted loads on this scan. Try again later."}
            {scanState === "done" && count === 0 && !hasTrucks && "Post a truck to see AI-matched loads for your lanes."}
          </p>

          <div className="grid grid-cols-3 gap-1.5">
            <div className="rounded-md px-2 py-1.5 bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-0.5 mb-0.5"><Truck className="h-2.5 w-2.5 text-muted-foreground" /></div>
              <span className="text-xs font-semibold text-foreground">{displayCount || "—"}</span>
              <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Matched</p>
            </div>
            <div className="rounded-md px-2 py-1.5 bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-0.5 mb-0.5"><MapPin className="h-2.5 w-2.5 text-muted-foreground" /></div>
              <span className="text-xs font-semibold text-foreground">{displayLanes || "—"}</span>
              <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Lanes</p>
            </div>
            <div className="rounded-md px-2 py-1.5 bg-muted/50 text-center">
              <div className="flex items-center justify-center gap-0.5 mb-0.5"><DollarSign className="h-2.5 w-2.5 text-muted-foreground" /></div>
              <span className="text-xs font-semibold text-foreground">{displayAvgRpm ? `$${displayAvgRpm.toFixed(2)}` : "—"}</span>
              <p className="text-[8px] text-muted-foreground uppercase tracking-wide">Avg RPM</p>
            </div>
          </div>

          {(scanState === "idle" || showDemo || (scanState === "done" && count > 0)) && (
            <div className="space-y-1">
              <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">
                {scanState === "done" && count > 0 ? "Top matches" : "Sample matches"}
              </span>
              {displayMatches.map((m) => (
                <div key={m.loadId} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: "hsl(145 70% 45%)" }} />
                    <span className="text-foreground font-medium truncate">{m.brokerName}</span>
                    <span className="text-muted-foreground truncate">— {m.originLocation} → {m.destinationLocation}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {m.rate ? (
                      <span className="text-[10px] font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                        ${m.rate.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">Call</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-auto pt-1">
            {scanState === "idle" && (
              <button
                onClick={runScan}
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
              >
                <Search className="h-3 w-3 mr-1" />
                Scan for Matches
                <ChevronRight className="h-3 w-3 ml-auto" />
              </button>
            )}
            {scanState === "loading" && (
              <button
                disabled
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 opacity-70 cursor-not-allowed"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
              >
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Scanning…
              </button>
            )}
            {scanState === "done" && count > 0 && (
              <button
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
              >
                View {count} Matched Load{count !== 1 ? "s" : ""}
                <ChevronRight className="h-3 w-3 ml-auto" />
              </button>
            )}
            {scanState === "done" && count === 0 && (
              <button
                onClick={runScan}
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
              >
                <Search className="h-3 w-3 mr-1" />
                Scan Again
                <ChevronRight className="h-3 w-3 ml-auto" />
              </button>
            )}
          </div>
        </div>
      </PostLoadCard>

      <MatchedLoadsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        matches={matches}
      />
    </>
  );
}

function extractState(location: string): string | null {
  if (!location) return null;
  const match = location.match(/,\s*([A-Z]{2})\b/);
  if (match) return match[1];
  const stateMatch = location.match(/\b([A-Z]{2})\b/);
  return stateMatch ? stateMatch[1] : null;
}
